/**
 * Jump To Section - Interactivity API View Script
 *
 * Replaces the previous jQuery implementation, which animated with a duration
 * of 0 (so the "smooth" setting never did anything), bound one unthrottled
 * scroll listener per button, and only auto-hid buttons whose target was "top".
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Whether the visitor asked for less motion.
 *
 * Checked per interaction rather than cached, so a mid-session OS change is
 * respected.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * How far the page can scroll, in pixels. Zero on pages shorter than the
 * viewport — callers must guard against dividing by it.
 *
 * @return {number} Scrollable distance.
 */
const getScrollRange = () =>
	document.documentElement.scrollHeight - window.innerHeight;

/**
 * Resolve the scroll destination for a block.
 *
 * @param {Object} ctx - Block context.
 * @return {number|null} Target offset in px, or null when the selector misses.
 */
const getDestination = ( ctx ) => {
	if ( 'bottom' === ctx.target ) {
		return getScrollRange();
	}

	if ( 'selector' === ctx.target ) {
		const node = document.querySelector( ctx.selector );

		// A selector that matches nothing must not scroll the page to 0 — that
		// would look like a broken jump to the top.
		return node ? node.getBoundingClientRect().top + window.scrollY : null;
	}

	return 0;
};

/**
 * Move keyboard focus to where the page just scrolled.
 *
 * Without this a keyboard user stays parked wherever they were, so the next Tab
 * continues from the bottom of the page — the scroll is purely visual for them.
 *
 * @param {Object} ctx - Block context.
 */
const moveFocus = ( ctx ) => {
	const node =
		'selector' === ctx.target
			? document.querySelector( ctx.selector )
			: document.querySelector( 'main, [role="main"]' );

	if ( ! node ) {
		return;
	}

	// Landmarks are not focusable by default; -1 makes them programmatically
	// focusable without adding them to the tab order.
	if ( ! node.hasAttribute( 'tabindex' ) ) {
		node.setAttribute( 'tabindex', '-1' );
	}

	// preventScroll, or the browser undoes the smooth scroll we just started.
	node.focus( { preventScroll: true } );
};

// One rAF gate per element, so N buttons on a page cannot queue N frames each.
const pending = new WeakMap();

/**
 * Recompute progress and visibility for one button.
 *
 * A plain function rather than a store method, because `this` inside an
 * Interactivity API callback is NOT the store — calling `this.callbacks.x()`
 * throws "Cannot read properties of undefined", which silently aborts directive
 * processing for the whole element and leaves the control completely inert.
 *
 * @param {Object}      ctx - Block context.
 * @param {HTMLElement} ref - The button element.
 */
const syncScrollState = ( ctx, ref ) => {
	if ( ! ref || pending.get( ref ) ) {
		return;
	}

	pending.set( ref, true );

	requestAnimationFrame( () => {
		pending.set( ref, false );

		const range = getScrollRange();
		const progress =
			range > 0 ? Math.min( 1, Math.max( 0, window.scrollY / range ) ) : 0;

		// Drives the progress-ring dash offset. Written even when the ring is
		// not the active style: it is one custom property, and keeping it
		// unconditional means no style-name checks in JS.
		ref.style.setProperty( '--brandy-jts-progress', progress );

		if ( ! ctx.isFixed || 0 === ctx.showAfter ) {
			ctx.hidden = false;
			return;
		}

		// showAfter is a percentage of the viewport height, so the threshold
		// scales with the screen instead of the old fixed 200px, which triggered
		// almost immediately on tall displays.
		ctx.hidden = window.scrollY < ( ctx.showAfter / 100 ) * window.innerHeight;
	} );
};

store( 'brandy/jump-to-section', {
	actions: {
		jump( event ) {
			event.preventDefault();

			const ctx = getContext();
			const top = getDestination( ctx );

			if ( null === top ) {
				return;
			}

			// 'instant', never 'auto': 'auto' defers to the CSS scroll-behavior
			// property, and this theme sets `html { scroll-behavior: smooth }`.
			// With 'auto' a reduced-motion visitor would still be animated.
			window.scrollTo( {
				top,
				behavior:
					'smooth' === ctx.motion && ! prefersReducedMotion()
						? 'smooth'
						: 'instant',
			} );

			moveFocus( ctx );
		},
	},

	callbacks: {
		/**
		 * Sync on load: a page restored mid-scroll (bfcache, an anchor link, a
		 * refresh) must show the right progress and visibility immediately.
		 */
		init() {
			syncScrollState( getContext(), getElement().ref );
		},

		onScroll() {
			syncScrollState( getContext(), getElement().ref );
		},
	},
} );
